import { useProfile } from "hooks/useProfile";
import { observer } from "mobx-react";
import type React from "react";
import { Navigate, useParams } from "react-router-dom";

import { FontAwesomeIcon } from "components/FontAwesomeIcon";
import {
  HstProductsWithPlaceholderModules,
  getComingSoonModules,
} from "constants/placeholder-constants";
import { GENERIC_ROUTES } from "constants/routes";
import { useSelectedProductId } from "hooks/useSelectedProductId";
import { useTranslation } from "react-i18next";
import { Heading2, Heading3, Heading4 } from "styles/elements/Headings";
import { ParagraphLarge, ParagraphMedium } from "styles/elements/Texts";
import { CssFlex } from "styles/helpers/layout";
import {
  StyledButton,
  StyledComingSoonChip,
  StyledContainer,
  StyledContent,
  StyledHeader,
  StyledImage,
  StyledLearningItem,
} from "./ComingSoonModulesPage.styled";

export const ComingSoonModulesPage: React.FC = observer(() => {
  const { userDetails } = useProfile();
  const { t } = useTranslation("homepage");
  const { moduleType } = useParams<{ moduleType: string }>();
  const selectedProductId = useSelectedProductId();

  const currentModule = getComingSoonModules(t).find((module) => module.type === moduleType);

  // If placeholder module name is not valid or current product doesn't have this feature
  // enabled, redirect to not found page
  if (
    !currentModule ||
    (selectedProductId && !HstProductsWithPlaceholderModules.includes(selectedProductId))
  ) {
    return <Navigate to={GENERIC_ROUTES.NOT_FOUND} />;
  }

  const handleSubscribeNewModules = (subscriptionUrl: string) => {
    if (subscriptionUrl) {
      window.open(subscriptionUrl, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <StyledContainer data-cy="coming-soon-modules-info">
      <StyledHeader>
        <CssFlex flexDirection="row" gap={1} alignItems="center">
          <Heading2 data-cy="coming-soon-module-title">{currentModule.title}</Heading2>
          <StyledComingSoonChip>
            <FontAwesomeIcon iconName="far fa-hourglass" iconSize="sm" />
            <ParagraphMedium>{t("studentDashboard.comingSoonModules.comingSoon")}</ParagraphMedium>
          </StyledComingSoonChip>
        </CssFlex>
        <ParagraphLarge>
          {t("studentDashboard.comingSoonModules.comingSoonModulesPage.welcome", {
            userName: userDetails?.firstName,
          })}
        </ParagraphLarge>
      </StyledHeader>
      <StyledContent>
        <StyledImage
          alt={t("studentDashboard.comingSoonModules.comingSoonModulesPage.imageAsset", {
            moduleName: currentModule.title,
          })}
          data-cy={`coming-soon-module-${currentModule.type}-image`}
          src={currentModule.imageAsset}
        />
        <CssFlex flexDirection="column" alignItems="flex-start" width="30rem">
          <CssFlex flexDirection="column" alignItems="flex-start" margin="2rem 0 1.5rem 0">
            <Heading3 marginBottom="1rem">{currentModule.title}</Heading3>
            {currentModule.learningItems.map((item, i) => (
              <StyledLearningItem key={`${currentModule.type}-${i}`}>
                <FontAwesomeIcon iconName={item.icon} iconSize="lg" />
                <ParagraphLarge>{item.text}</ParagraphLarge>
              </StyledLearningItem>
            ))}
          </CssFlex>

          <Heading4 marginBottom="1rem">
            {t("studentDashboard.comingSoonModules.comingSoonModulesPage.firstToKnow")}
          </Heading4>
          <ParagraphLarge>
            {t("studentDashboard.comingSoonModules.comingSoonModulesPage.getNotified")}
          </ParagraphLarge>
          <StyledButton
            variant="primary"
            onClick={() => handleSubscribeNewModules(currentModule.subscriptionUrl)}
          >
            {t("studentDashboard.comingSoonModules.comingSoonModulesPage.getNotifiedBtn")}
          </StyledButton>
        </CssFlex>
      </StyledContent>
    </StyledContainer>
  );
});
